import { forwardRef, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { DisciplinaArquivoForm, DisciplinaArquivoSchema } from "../../../schemas/Projetos_cadastro/Disciplina_Arquivos";
import { DefaultFormDownloadInput } from "../../../../../../common/components/default_form_download_input";
import { ModalLoading } from "../../../../../../common/components/modal_loading";

type Props = {
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onSubmit: (form: DisciplinaArquivoForm) => void;
  dados?: DisciplinaArquivoForm | null;
  editmod: boolean;
};

export const DisciplinaArquivoModelContent = forwardRef<HTMLButtonElement, Props>(
  ({ open, onClose, saving, onSubmit, dados, editmod }, ref) => {

    const hookForm = useForm<DisciplinaArquivoForm>({
      mode: "onSubmit",
      reValidateMode: "onSubmit",
      resolver: zodResolver(DisciplinaArquivoSchema),
    });

    useEffect(() => {
      if (dados) {
        hookForm.reset({
          ...dados,
          limparArquivo1: false,
          limparArquivo2: false
        });
      } else {
        hookForm.reset({
          limparArquivo1: false,
          limparArquivo2: false
        });
      }
    }, [dados, hookForm]);

    const nomeArquivo = hookForm.watch("nome");
    const nomeArquivo2 = hookForm.watch("nome2");
    const descricao = hookForm.watch("descricao");

    const arquivo = hookForm.watch("arquivo");
    const arquivo2 = hookForm.watch("arquivo2");

    const handleSubmit = useCallback(
      (form: DisciplinaArquivoForm) => {
        if (!saving) {
          onSubmit(form);
        }
      },
      [saving, onSubmit],
    );

    const handleClose = useCallback(() => {
      if (!saving) onClose();
    }, [saving, onClose]);

    useEffect(() => {
      if (arquivo instanceof File) {
        hookForm.setValue("nome", arquivo.name, { shouldDirty: true });
        hookForm.setValue("limparArquivo1", false);
      }
    }, [arquivo, hookForm]);

    useEffect(() => {
      if (arquivo2 instanceof File) {
        hookForm.setValue("nome2", arquivo2.name, { shouldDirty: true });
        hookForm.setValue("limparArquivo2", false);
      }
    }, [arquivo2, hookForm]);

    const handleDeleteFile1 = () => {
      hookForm.setValue("limparArquivo1", true, { shouldDirty: true, shouldValidate: true });
      hookForm.setValue("nome", "");
      hookForm.setValue("arquivo", null);
    };

    const handleDeleteFile2 = () => {
      hookForm.setValue("limparArquivo2", true, { shouldDirty: true, shouldValidate: true });
      hookForm.setValue("nome2", "");
      hookForm.setValue("arquivo2", null);
    };

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            component: "form",
            onSubmit: hookForm.handleSubmit(
              handleSubmit,
              (erros) => console.log("🚨 ERROS DE VALIDAÇÃO ZOD:", erros)
            ),
          },
        }}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": { width: "100%", maxWidth: "46rem" },
          },
        }}
      >
        <DialogTitle>Arquivos - {descricao}</DialogTitle>
        <DialogContent>
          <ModalLoading loading={saving}>

            <Box display="flex" flexDirection="column" gap={3}>

              {/* INPUT DO ARQUIVO 1 */}
              <DefaultFormDownloadInput
                name="arquivo"
                nameLimparFlag="limparArquivo1"
                saving={saving}
                filename={nomeArquivo ?? null}
                reactHookForm={hookForm}
                onDeleteFile={handleDeleteFile1}
              />

              {/* INPUT DO ARQUIVO 2 */}
              <DefaultFormDownloadInput
                name="arquivo2"
                nameLimparFlag="limparArquivo2"
                saving={saving}
                filename={nomeArquivo2 ?? null}
                reactHookForm={hookForm}
                onDeleteFile={handleDeleteFile2}
              />

            </Box>
          </ModalLoading>

        </DialogContent>
        <DialogActions>
          <Box className="w-full flex justify-end gap-4 p-4">
            <Box className="flex gap-1">
              <Box className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                <h1 className="text-sm text-black dark:text-white" onClick={handleClose}>
                  Cancelar
                </h1>
              </Box>
              <Box width="120px">
                {saving ? (
                  <PrimaryButton buttonColor="primary-green">Salvando...</PrimaryButton>
                ) : (
                  <PrimaryButton type="submit" buttonColor="primary-green">
                    Salvar
                  </PrimaryButton>
                )}
              </Box>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    );
  },
);